import { CSSProperties, Fragment, useEffect, useState } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import { Alert } from 'reactstrap';
import { ColumnType, WebEntity, WebEntityName } from '../../entities/types';
import { AuthState, EntitiesState, ErrorState } from '../../reducers/types';
import { BasicEntity, UserTypeValues } from '../../types';
import { capitalize } from '../../utils/utils';
import { toast } from 'react-toastify';
import { MRT_RowData, MRT_TableInstance } from 'material-react-table';
import {
  AdditionalTableActionsFunc,
  AdditionalTableProps,
  AdditionalTopTableComponents,
  BodyRowProps,
  CRUDAllowedActions,
  InitialState
} from '../types';
import GenericModalSidebar from './GenericModalSidebar';
import GenericModalMultiInputForm from './GenericModalMultiInputForm';
import GenericTable from './GenericTable';
import Swal from 'sweetalert2';
import { Button, IconButton } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import GenericIcons from './GenericIcons';
import '../../css/AppHome.css';
import '../../css/vdrGenerics.css';

// Colores de los botones de SweetAlert2 (marca): confirmar en cian, eliminar en rojo y
// cancelar como botón de borde (el borde y el texto los pone vdrGenerics.css).
const SWAL_CONFIRM_COLOR = '#00b0d9';
const SWAL_DANGER_COLOR = '#c62828';
const SWAL_CANCEL_COLOR = 'transparent';

type Props<T> = {
  // Global States
  entities: EntitiesState;
  error: ErrorState;
  auth: AuthState;

  // Redux Actions
  getEntities: (pageNumber?: number, pageSize?: number, filters?: any) => Promise<any>;
  deleteEntity: (id: string[]) => any;
  addEntity: (entity: T | T[]) => any;
  multiAddEntity: (entity: T | T[]) => any;
  editEntity: (entity: T) => any;
  notifyMessageEntity: (msg: string) => void;

  // Aditional
  webEntity: WebEntity<T>;
  title: string;
  signalsToWatch: string[];
  allowedActions?: Partial<CRUDAllowedActions<T>>;
  serverSidePagination?: boolean;
  enablePagination?: boolean;
  additionalTableProps?: AdditionalTableProps<T>;
  additionalTableActions?: AdditionalTableActionsFunc<T>;
  addMultiLineAction?: JSX.Element;
  preProcessEntityList?: (entityList: Array<any>) => Array<any>;
  customExport?: {
    exportDataHandler: (when?: string) => Promise<any[]>;
    columnTypes: ColumnType[];
    fileName?: string;
  };
  initialState?: InitialState;
  enableColumnVirtualization?: boolean;
  enableRowVirtualization?: boolean;
  optionsPagination?: {
    initialStatePagination?: { pagination: { pageSize: number; pageIndex: number } };
  };
  columnsHeaderStyle?: CSSProperties;
  style?: CSSProperties;
  additionalTopTableActions?: AdditionalTopTableComponents[];
  enableStickyFooter?: boolean;
  bodyRowProps?: BodyRowProps;
};

const GenericCRUD = <T,>({
  entities,
  error,
  auth,
  getEntities,
  deleteEntity,
  addEntity,
  multiAddEntity,
  editEntity,
  notifyMessageEntity,
  webEntity,
  title,
  signalsToWatch,
  allowedActions,
  serverSidePagination,
  additionalTableProps,
  enablePagination,
  additionalTableActions,
  preProcessEntityList,
  customExport,
  columnsHeaderStyle,
  style,
  initialState,
  enableColumnVirtualization,
  enableRowVirtualization,
  optionsPagination,
  additionalTopTableActions,
  enableStickyFooter,
  bodyRowProps
}: Props<T>) => {
  const [errorMsg, setErrorMsg] = useState<string>('');
  const {
    list: entityList,
    loading,
    notificationMessage
  } = entities[webEntity.name as WebEntityName];

  const [forceRefresh, setForceRefresh] = useState<boolean>(false);

  useEffect(() => {
    if (error) {
      setErrorMsg(error.msg.msg);
      setTimeout(() => setErrorMsg(''), 5000);
    }

    if (notificationMessage) {
      setTimeout(() => notifyMessageEntity(''), 5000);
    }
  }, [error, notificationMessage, notifyMessageEntity]);

  const { isAuthenticated, user } = auth;
  const isAdmin = user?.userTypeName === UserTypeValues.Admin || false;

  const onAddEntity =
    () =>
    async (fields: T | T[]): Promise<any> => {
      const swalResult = await Swal.fire({
        icon: 'question',
        title: `¿Confirmas los datos ingresados?`,
        showCancelButton: true,
        confirmButtonColor: SWAL_CONFIRM_COLOR,
        cancelButtonColor: SWAL_CANCEL_COLOR,
        confirmButtonText: 'Sí',
        cancelButtonText: 'Cancelar',
        customClass: {
          container: 'swal-custom-zindex'
        }
      });

      if (swalResult.isConfirmed) {
        try {
          const result = await toast.promise(
            async () => {
              const res = await addEntity(fields);
              if (res) {
                return res;
              } else {
                throw error;
              }
            },
            {
              pending: 'Cargando...',
              success: `Se insertó correctamente el registro`,
              error: 'Error, no se pudo insertar el registro'
            }
          );
          return result;
        } catch (error) {
          console.log(error);
          return null;
        }
      }

      return null;
    };

  const onMultiAddEntity = () => async (fields: T | T[]) => {
    toast.promise(
      async () => {
        const res = await multiAddEntity(fields);
        if (res && res.status === 200) {
          return res;
        } else {
          throw error;
        }
      },
      {
        pending: 'Cargando...',
        success: `Se insertaron correctamente los registros`,
        error: 'Error, no se pudieron insertar los registros'
      }
    );
  };

  const onDeleteEntity =
    (
      getSelectedData: () => { ids: string[]; refs: string },
      table: MRT_TableInstance<MRT_RowData>
    ) =>
    async () => {
      const { ids, refs } = getSelectedData();

      Swal.fire({
        icon: 'question',
        title: refs,
        showCancelButton: true,
        confirmButtonColor: SWAL_DANGER_COLOR,
        cancelButtonColor: SWAL_CANCEL_COLOR,
        confirmButtonText: 'Sí',
        cancelButtonText: 'Cancelar',
        customClass: {
          container: 'swal-custom-zindex',
          confirmButton: 'vdr-gen-swal-danger'
        }
      }).then(async (result) => {
        if (result.isConfirmed) {
          toast.promise(
            async () => {
              const res = await deleteEntity(ids);
              table.resetRowSelection();
              if (res) {
                //notifyMessageEntity(`Se han elimando correctamente los siguientes datos: ${res}`);
                if (serverSidePagination) {
                  setForceRefresh(!forceRefresh);
                }
                return res;
              } else {
                //notifyMessageEntity(`La accion no se ha completado correctamente`);
                throw error;
              }
            },
            {
              pending: 'Cargando...',
              success: {
                render({ data }) {
                  return `Se elimino correctamente el registro: ${data}`;
                }
              },
              error: 'Error, no se puede eliminar el registro'
            }
          );
        }
      });
    };

  const onEditEntity =
    (row: BasicEntity) =>
    async (updatedEntity: T): Promise<any> => {
      const swalResult = await Swal.fire({
        icon: 'question',
        title: `¿Seguro que deseas continuar con la edición?`,
        showCancelButton: true,
        confirmButtonColor: SWAL_CONFIRM_COLOR,
        cancelButtonColor: SWAL_CANCEL_COLOR,
        confirmButtonText: 'Sí',
        cancelButtonText: 'Cancelar',
        customClass: {
          container: 'swal-custom-zindex'
        }
      });

      if (swalResult.isConfirmed) {
        try {
          const result = await toast.promise(
            async () => {
              const res = await editEntity({ _id: row._id, ...updatedEntity });
              if (res) {
                if (
                  res.successMessage &&
                  res.successMessage.icon &&
                  res.successMessage.title &&
                  res.successMessage.html
                ) {
                  Swal.fire({
                    icon: res.successMessage.icon,
                    title: res.successMessage.title,
                    html: res.successMessage.html
                  });
                }
                return res;
              } else {
                throw error;
              }
            },
            {
              pending: 'Cargando...',
              success: {
                render({ data }) {
                  const referenceColumn = data.hasOwnProperty(webEntity?.referenceColumn)
                    ? data[webEntity.referenceColumn]
                    : row._id;
                  return `Se editó correctamente el registro: ${referenceColumn}`;
                }
              },
              error: 'Error, no se pudo editar el registro'
            }
          );

          return result;
        } catch (error) {
          console.log(error);
        }
      }

      return null;
    };

  const renderAddButton = (): JSX.Element => (
    <GenericModalSidebar
      webEntity={webEntity}
      actionFn={onAddEntity()}
      isAuthenticated={isAuthenticated}
      isAdmin={isAdmin}
      alternativeButton={(handleOpen) => (
        <button
          type='button'
          onClick={handleOpen}
          className='generic-crud-add-button vdr-gen-add-btn'
          aria-label={webEntity.customButtonNames?.addButtonName || 'Agregar'}>
          {webEntity.customButtonNames?.addButtonName || ''}
          <span className='generic-crud-add-button-icon-container'>
            <GenericIcons icon='plusWhiteIcon' imageStyle={{ height: '100%' }} />
          </span>
        </button>
      )}
      title={webEntity.customDataActions?.addModalTitle}
    />
  );

  const renderAdditionalTopTableActions = (): JSX.Element => (
    <Fragment>
      {additionalTopTableActions?.map((a, idx) => (
        <Fragment key={idx}>{a.component}</Fragment>
      ))}
    </Fragment>
  );

  const renderAddMultiLineButton = (): JSX.Element => (
    <GenericModalMultiInputForm
      webEntity={webEntity}
      actionFn={onMultiAddEntity()}
      errorMsg={errorMsg}
      isAuthenticated={isAuthenticated}
      isAdmin={isAdmin}
    />
  );

  const renderEditButton = (row: BasicEntity): JSX.Element => (
    <GenericModalSidebar
      webEntity={webEntity}
      isAuthenticated={isAuthenticated}
      isAdmin={isAdmin}
      actionFn={onEditEntity(row)}
      alternativeButton={(toggle: () => void) => (
        <IconButton
          onClick={toggle}
          sx={{
            color: 'var(--vdr-text-2)',
            '&:hover': { color: 'var(--vdr-ink)', backgroundColor: 'var(--vdr-cyan-tint)' }
          }}>
          <EditIcon fontSize='small' />
        </IconButton>
      )}
      entityToEdit={row}
      title={webEntity.customDataActions?.editModalTitle || `Editar ${webEntity.name}`}
      actionName={'Editar'}
    />
  );

  const renderDeleteButton = (
    getSelectedData: () => { ids: string[]; refs: string },
    table: MRT_TableInstance<MRT_RowData>
  ): JSX.Element => (
    <Button
      onClick={onDeleteEntity(getSelectedData, table)}
      variant='contained'
      color='error'
      sx={{ gap: 1, minHeight: 40, px: 2.25, fontFamily: 'var(--vdr-font-display)' }}>
      <DeleteIcon fontSize='small' />
      {webEntity.customButtonNames?.deleteButtonName || 'Eliminar'}
    </Button>
  );

  const preProcessedEntityList = preProcessEntityList
    ? preProcessEntityList(entityList)
    : entityList;

  const allowedTableActions = {
    add: isAuthenticated && allowedActions?.add,
    multiLineForm: isAuthenticated && allowedActions?.multiLineForm,
    delete: isAuthenticated && allowedActions?.delete,
    edit: isAuthenticated && allowedActions?.edit,
    editCondition: allowedActions?.editCondition,
    export: isAuthenticated && allowedActions?.export,
    select: isAuthenticated && allowedActions?.select
  };

  return (
    <div style={style ? style : {}}>
      {errorMsg && signalsToWatch.includes(error.id!) && (
        <Alert color='danger' className='vdr-gen-alert' style={{ marginTop: '1vw' }}>
          {errorMsg}
        </Alert>
      )}

      {notificationMessage && (
        <Alert color='info' className='vdr-gen-alert' style={{ marginTop: '1vw' }}>
          {notificationMessage}
        </Alert>
      )}

      <GenericTable
        columns={webEntity.tableColumns}
        tableData={preProcessedEntityList}
        fetchData={getEntities}
        addAction={renderAddButton()}
        addMultiLineAction={renderAddMultiLineButton()}
        deleteAction={renderDeleteButton}
        allowedActions={allowedTableActions}
        tableName={capitalize(title)}
        secondaryAction={additionalTableActions}
        additionalTableProps={additionalTableProps}
        customExport={customExport}
        initialState={initialState}
        columnsHeaderStyle={columnsHeaderStyle}
        editAction={renderEditButton}
        enablePagination={enablePagination}
        enableColumnVirtualization={enableColumnVirtualization}
        enableRowVirtualization={enableRowVirtualization}
        optionsPagination={optionsPagination}
        loadingTable={loading}
        additionalTopTableActions={renderAdditionalTopTableActions()}
        enableStickyFooter={enableStickyFooter}
        bodyRowProps={bodyRowProps}
      />
    </div>
  );
};

export default GenericCRUD;
